import React from 'react';

import { Translate } from 'react-localize-redux';

import styled, { useTheme } from 'styled-components';

import Flex from '@components/layout/flex';
import Typography from '@components/basics/typography';

import { SongNumber } from '@components/catalog-row/desktop-view/track-row';
import Grid from '@components/basics/grid';
import Pill from '@components/basics/pill';
import CircleArrowIcon from '@components/basics/icons/circle-arrow';
import { AnimatePresence } from 'framer-motion';
import { ReleaseStatus } from '@common/types/release/relase-status';
import { getTrackCollaborators } from '@webapp/utils/tracks/get-track-collaborators';
import AvatarPreview from '@components/layout/preview-box/avatar-preview';
import CatalogTrack from '@common/types/catalog/catalog-track';

interface MobileTrackRowProps {
  track: CatalogTrack;
  releaseStatus: ReleaseStatus;
  isReleases: boolean;
  isArtist?: boolean;
  artistId?: string;
}

export default function MobileTrackRow({
  track,
  releaseStatus,
  isArtist,
  artistId,
}: MobileTrackRowProps) {
  const theme = useTheme();
  const [openSong, setOpenSong] = React.useState('');
  const { trackArtists, trackCredits } = React.useMemo(() => {
    const { collaborators, contributors } = getTrackCollaborators(
      track,
      artistId
    );
    const trackArtists =
      releaseStatus !== 'draft'
        ? collaborators.map(({ name, avatar }) => ({
            name,
            avatar,
          }))
        : [];
    const trackCredits =
      releaseStatus !== 'draft'
        ? contributors.map(({ name, avatar }) => ({
            name,
            avatar,
          }))
        : [];
    return { trackArtists, trackCredits };
  }, [track, artistId, releaseStatus]);

  return (
    <Wrapper>
      <HeaderRow alignItems="center" gap={10}>
        <SongContainer>
          <SongNumber
            className={track.isNFT ? 'nft' : ''}
            justifyContent="center"
            alignItems="center"
          >
            <Typography className="number" type="small" weight="700">
              {track.order}
            </Typography>
          </SongNumber>
          <Grid gap={3}>
            <Typography
              className={track.isNFT ? 'nft' : ''}
              customFont="semi-bold"
            >
              {track.name.length > 20
                ? `${track.name.slice(0, 20)}...`
                : track.name}
            </Typography>
            <Typography type="small" color={theme.colors.tertiary}>
              {track.isrc ? track.isrc : '-'}
            </Typography>
          </Grid>
        </SongContainer>
        <StyledCircleArrowIcon
          arrowUp={openSong === track.id}
          disabled={trackArtists.length === 0}
          onClick={() => setOpenSong(openSong ? '' : track.id)}
        />
      </HeaderRow>
      {track.isNFT && <Pill>NFT</Pill>}
      {openSong === track.id && (
        <AnimatePresence exitBeforeEnter>
          <CollaboratorsContainer>
            <Grid>
              <Flex justifyContent="space-between" alignItems="center">
                <Typography
                  weight="500"
                  customFont="medium"
                  color="#A1A5B4"
                  uppercase
                >
                  <Translate
                    id={
                      isArtist
                        ? 'CATALOG.TRACKS.HEADER.FEAT_ARTISTS'
                        : 'CATALOG.TRACKS.HEADER.OWNERSHIP'
                    }
                  />
                </Typography>
              </Flex>
              <ListWrapper>
                <AvatarPreview
                  items={trackArtists}
                  avatarSize="24"
                  breakingIndex={6}
                />
                <Flex alignItems="center" gap={3}>
                  {!!trackArtists.length &&
                    trackArtists.map((item, i) => (
                      <Typography weight="700" key={item.name}>
                        {`${item.name}${
                          trackArtists.length !== i + 1 ? ',' : '.'
                        }`}
                      </Typography>
                    ))}
                </Flex>
              </ListWrapper>
            </Grid>
            <Grid>
              <Flex justifyContent="space-between" alignItems="center">
                <Typography
                  weight="500"
                  customFont="medium"
                  color="#A1A5B4"
                  uppercase
                >
                  <Translate
                    id={
                      isArtist
                        ? 'CATALOG.TRACKS.HEADER.CREDITS'
                        : 'CATALOG.TRACKS.HEADER.STAKEHOLDERS'
                    }
                  />
                </Typography>
              </Flex>

              <ListWrapper>
                <AvatarPreview
                  items={trackCredits}
                  avatarSize="24"
                  breakingIndex={6}
                  noAvatar
                />
                <Flex alignItems="center" gap={3}>
                  {!!trackCredits.length &&
                    trackCredits.map((item, i) => (
                      <Typography weight="700" key={item.name}>
                        {`${item.name}${
                          trackCredits.length !== i + 1 ? ',' : '.'
                        }`}
                      </Typography>
                    ))}
                </Flex>
              </ListWrapper>
            </Grid>
          </CollaboratorsContainer>
        </AnimatePresence>
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.colors.box};
  &:not(:last-child) {
    border-bottom: none;
  }
`;

const StyledCircleArrowIcon = styled(CircleArrowIcon)`
  width: 24px;
  height: 24px;
`;

const ListWrapper = styled.div`
  display: grid;
  grid-gap: 5px;
  margin: 5px 0 10px;
  padding: 0 16px;
`;

const SongContainer = styled(Flex)``;

const CollaboratorsContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 16px;
`;

const HeaderRow = styled(Flex)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 8px 16px;
`;
